import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Moment } from 'moment';
import type { Office, UserPayload, UserSimple, Uuid } from './types';

export interface State {
  uuid: Uuid;
  name: string;
  cities: string[];
}

export interface Country {
  uuid: Uuid;
  name: string;
  states: State[];
}

export interface NamedUuid {
  uuid: Uuid;
  name: string;
}

export interface SocialNetwork {
  network: string;
  link: string;
}

export interface CandidateExperience {
  company: string;
  position: string | null;
  worked_from: string | null;
  worked_till: string | null;
  is_current: boolean;
  additional_info: string | null;
}

export interface CandidateSource {
  source: string;
  additional_source_name: string;
  is_main: boolean;
}

export interface CandidateSpecialityLevel {
  level: string;
  uuid: Uuid;
  candidate_id?: Uuid;
  speciality: string | null;
}

export interface CandidateForeignLanguage {
  language: string;
  level: string;
}

export interface CandidateDocument {
  uuid: Uuid;
  name?: string;
  filename?: string;
  uploaded?: string;
  link?: string | boolean;
  file?: string;
}

export interface CandidateData {
  uuid?: Uuid;
  cvs: Array<Uuid | CandidateDocument>;
  cvs_links: string[];
  resume_links?: string[];
  first_name_latin?: string;
  last_name_latin?: string;
  first_name_local?: string;
  last_name_local?: string;
  description?: string | null;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  timezone?: string;
  is_ready_to_relocate?: boolean;
  is_ready_to_business_trips?: boolean;
  is_self_submitted?: boolean;
  telegram: string[];
  email: string[];
  phone: string[];
  social_networks: SocialNetwork[];
  foreign_languages: CandidateForeignLanguage[];
  experience: CandidateExperience[];
  sources: CandidateSource[];
  status?: string;
  tags?: string[];
  specialities: CandidateSpecialityLevel[];
  offices?: Office[];
  /* Legacy fields kept on initial state for backward compatibility with
     pre-existing consumers that referenced them; no current endpoint populates them. */
  technologies: unknown[];
  assignments: unknown[];
}

export interface Reminder {
  uuid: Uuid;
  candidate: Uuid;
  author: UserPayload;
  recruiter: UserSimple;
  datetime: string;
  type: string;
  comment?: string | null;
  in_progress: boolean;
  create_google_event: boolean;
  group_name?: string;
}

export interface ReminderPayload {
  candidate: Uuid;
  recruiter?: Uuid;
  comment: string;
  type?: string;
  create_google_event: boolean;
  in_progress: boolean;
  datetime: Moment;
}

export interface CandidateSettings {
  countries: Country[];
  companies: NamedUuid[];
  tags: NamedUuid[];
  specialities: NamedUuid[];
}

export interface CandidateTimeline {
  data: unknown[];
  count: number;
  sorting: 'asc' | 'desc';
  filter: string;
  offset?: number;
}

export interface CandidateJobAssignment {
  uuid: Uuid;
  job: string;
  job_uuid: Uuid;
  candidate_status?: string;
  active_step: { uuid: Uuid; name: string; step_status: string } | null;
  team_lead: string;
  responsible_recruiter: string | null;
}

export interface CandidateListItem {
  uuid: Uuid;
  full_name_latin?: string;
  full_name_local?: string;
  status?: string;
  is_self_submitted?: boolean;
}

export interface DuplicateCandidate {
  uuid: Uuid;
  full_name_latin?: string;
  full_name_local?: string;
}

export interface ResponsibleRecruiter {
  uuid: Uuid;
  full_name: string;
}

export interface CandidateSourceRow {
  source: string;
}

export interface SSCWrappedCandidate {
  candidate_profile: CandidateData;
}

export interface CandidateInteractionPayload {
  candidate: Uuid;
  recruiter?: Uuid;
  comment: string;
  activity?: string;
  type?: string;
  in_progress: boolean;
  datetime: Moment;
}

export interface CandidateInteraction {
  uuid: Uuid;
  candidate: Uuid;
  author: { uuid: Uuid };
  recruiter: { uuid: Uuid };
  datetime: string;
  type: string;
  comment?: string | null;
  in_progress: boolean;
}

export interface CandidateState {
  filter: Record<string, unknown>;
  settings: CandidateSettings;
  reminders: Reminder[];
  recruiters: Array<{ uuid: Uuid; full_name: string }>;
  jobs: CandidateJobAssignment[];
  jobsLoading: boolean;
  interactions: Record<string, unknown>;
  candidateData: CandidateData;
  timeline: CandidateTimeline;
  candidateDuplicates: Array<{ uuid: Uuid; full_name_latin?: string; full_name_local?: string }>;
  sources: Array<{ value: string; label: string }>;
}

const emptyCandidateData: CandidateData = {
  phone: [],
  email: [],
  telegram: [],
  technologies: [],
  social_networks: [],
  assignments: [],
  experience: [],
  cvs: [],
  cvs_links: [],
  specialities: [],
  sources: [],
  foreign_languages: [],
};

export const initialState: CandidateState = {
  filter: {},
  settings: {
    countries: [],
    companies: [],
    tags: [],
    specialities: [],
  },
  reminders: [],
  recruiters: [],
  jobs: [],
  jobsLoading: false,
  interactions: {},
  candidateData: emptyCandidateData,
  timeline: {
    data: [],
    count: 0,
    sorting: 'desc',
    filter: '',
  },
  candidateDuplicates: [],
  sources: [],
};

type SetCandidateTimelinePayload = { clear: true } | { data: unknown[]; count: number };

interface SetCandidateTimelineFiltersPayload {
  filter: string;
  sorting: 'asc' | 'desc';
}

const candidateSlice = createSlice({
  name: 'candidate',
  initialState,
  reducers: {
    changeCandidateFilter(state, { payload }: PayloadAction<Record<string, unknown>>) {
      state.filter = payload;
    },
    setCountries(state, { payload }: PayloadAction<Country[]>) {
      state.settings.countries = payload;
    },
    setResponsibleRecruiter(state, { payload }: PayloadAction<Array<{ uuid: Uuid; full_name: string }>>) {
      state.recruiters = payload;
    },
    setCompanies(state, { payload }: PayloadAction<NamedUuid[]>) {
      state.settings.companies = payload;
    },
    setSpecialties(state, { payload }: PayloadAction<NamedUuid[]>) {
      state.settings.specialities = payload;
    },
    setTags(state, { payload }: PayloadAction<NamedUuid[]>) {
      state.settings.tags = payload;
    },
    setDuplicateCandidates(state, { payload }: PayloadAction<CandidateState['candidateDuplicates']>) {
      state.candidateDuplicates = payload;
    },
    setCandidateData(state, { payload }: PayloadAction<CandidateData>) {
      state.candidateData = payload;
    },
    setRemindersList(state, { payload }: PayloadAction<Reminder[]>) {
      state.reminders = payload;
    },
    setJobsList(state, { payload }: PayloadAction<CandidateJobAssignment[]>) {
      state.jobs = payload;
    },
    setCandidateJobsListLoading(state, { payload }: PayloadAction<boolean>) {
      state.jobsLoading = payload;
    },
    setInteractions(state, { payload }: PayloadAction<Record<string, unknown>>) {
      state.interactions = payload;
    },
    setCandidateTimeline(state, { payload }: PayloadAction<SetCandidateTimelinePayload>) {
      if ('data' in payload) {
        const data = [...state.timeline.data, ...payload.data];
        state.timeline.data = data;
        state.timeline.offset = data.length;
        state.timeline.count = payload.count;
      } else {
        state.timeline.data = [];
        state.timeline.offset = 0;
        state.timeline.count = 0;
      }
    },
    setCandidateTimelineFilters(state, { payload }: PayloadAction<SetCandidateTimelineFiltersPayload>) {
      state.timeline.filter = payload.filter;
      state.timeline.sorting = payload.sorting;
      state.timeline.offset = 0;
    },
    setCandidateDocuments(state, { payload }: PayloadAction<CandidateDocument[]>) {
      state.candidateData.cvs = payload;
    },
    setSources(state, { payload }: PayloadAction<CandidateState['sources']>) {
      state.sources = payload;
    },
    clearCandidateData(state) {
      state.candidateData = { ...emptyCandidateData };
    },
    resetCandidateReducer(_state) {
      _state = initialState;
    },
  },
});

export const {
  changeCandidateFilter,
  setCountries,
  setResponsibleRecruiter,
  setCompanies,
  setSpecialties,
  setTags,
  setDuplicateCandidates,
  setCandidateData,
  setRemindersList,
  setJobsList,
  setCandidateJobsListLoading,
  setInteractions,
  setCandidateTimeline,
  setCandidateTimelineFilters,
  setCandidateDocuments,
  clearCandidateData,
  setSources,
  resetCandidateReducer,
} = candidateSlice.actions;
export default candidateSlice.reducer;
